/**
 * Sentinel on a running mission: the threat strip and the conversation.
 *
 * Findings and the write-up live on the Report tab. Compare is the start
 * briefing. This column is only what an operator asks while they drive.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { Mission, MissionRouteStatus } from '../../services/missionApi';
import MissionSentinelChat from './MissionSentinelChat';
import { RouteOptionPreview } from './map/MissionOptionPreviewLayer';
import { formatClock } from './format';
import DockStatStrip from './DockStatStrip';
import { DockTabHelp } from './DockTabHelp';
import { buildAlertFeed } from './alertWaterfall';
import DevicesIcon from '@mui/icons-material/Devices';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CampaignIcon from '@mui/icons-material/Campaign';
import AltRouteIcon from '@mui/icons-material/AltRoute';

const GOLD = '#f5b942';
const RED = '#ef5350';
const TEAL = '#00b8a9';
const CORAL = '#ff6b5e';

const STRIP: Record<string, { color: string; bg: string; border: string }> = {
  clear: { color: TEAL, bg: 'rgba(0,184,169,0.10)', border: '#1a3d38' },
  watch: { color: GOLD, bg: 'rgba(245,185,66,0.10)', border: '#3d3319' },
  elevated: { color: GOLD, bg: 'rgba(245,185,66,0.10)', border: '#3d3319' },
  high: { color: CORAL, bg: 'rgba(255,107,94,0.10)', border: '#4a2a26' },
  critical: { color: RED, bg: 'rgba(239,83,80,0.12)', border: '#5c2b29' },
};

export interface MissionSentinelProps {
  mission: Mission;
  onError: (message: string | null) => void;
  onMissionChange: () => void;
  refreshKey?: number;
  offRoute?: boolean;
  status?: MissionRouteStatus | null;
  onPreviewOption?: (preview: RouteOptionPreview | null) => void;
  /** Fill the dock tab so the thread scrolls as a running log. */
  fill?: boolean;
}

const MissionSentinel: React.FC<MissionSentinelProps> = ({
  mission, onError, onMissionChange, refreshKey, offRoute, status, onPreviewOption, fill,
}) => {
  const level = status?.threat_level || null;
  const strip = level ? STRIP[level] : null;
  const feed = buildAlertFeed(status?.events || [], () => '');
  const liveDevices = (status?.devices || []).filter((d) => d.signal_state !== 'lost').length;

  return (
    <Box sx={{
      mt: 0.25,
      ...(fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
    }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.85,
          mb: 0.75,
          px: 1,
          py: 0.65,
          borderRadius: 0.75,
          bgcolor: strip?.bg || 'rgba(255,255,255,0.03)',
          border: '1px solid',
          borderColor: strip?.border || 'rgba(255,255,255,0.08)',
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            flex: 'none',
            bgcolor: strip?.color || '#5b6570',
            boxShadow: strip ? `0 0 8px ${strip.color}` : 'none',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            fontSize: '0.62rem',
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: strip?.color || 'text.secondary',
          }}
        >
          {level || 'threat'}
        </Typography>
        <Typography
          variant="caption"
          noWrap
          title={status?.threat_level_reason || undefined}
          sx={{ flex: 1, minWidth: 0, fontSize: '0.62rem', color: 'text.secondary' }}
        >
          {status?.threat_level_reason_short || status?.threat_level_reason || 'Waiting on the monitor.'}
        </Typography>
        {status?.threat_level_at && (
          <Typography variant="caption" sx={{ fontSize: '0.52rem', color: 'text.disabled' }}>
            {formatClock(status.threat_level_at)}
          </Typography>
        )}
        <DockTabHelp tab="sentinel" />
      </Box>

      <DockStatStrip
        items={[
          { label: 'Open', value: feed.openCount, color: feed.openCount ? CORAL : '#8a939f', icon: <WarningAmberIcon /> },
          { label: 'Standing', value: feed.standing.length, color: GOLD, icon: <CampaignIcon /> },
          { label: 'Devices', value: liveDevices, color: TEAL, icon: <DevicesIcon /> },
          { label: 'Off route', value: offRoute ? 1 : 0, color: offRoute ? CORAL : '#8a939f', icon: <AltRouteIcon /> },
        ]}
      />

      <MissionSentinelChat
        mission={mission}
        onError={onError}
        onMissionChange={onMissionChange}
        refreshKey={refreshKey}
        offRoute={offRoute}
        status={status}
        onPreviewOption={onPreviewOption}
        fill={fill}
      />
    </Box>
  );
};

export default MissionSentinel;
